import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { concat, from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DatabaseService } from '../../providers/database.service';
import { DatabaseModel, MotesPaths } from '../../models';

import { BlogPost } from './blog-post';
import { ResolverItemStream } from '@motes-sdk/providers/data-resolve.service';
import { ActivatedRouteSnapshot } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class BlogPostService {

    constructor(private db: DatabaseService, @Inject(LOCALE_ID) public locale: string) {
    }

    watchPostList(): Observable<BlogPost[]> {
        return this.db.list<BlogPost>(MotesPaths.POST_DB_PATH()).snapshotChanges().pipe(map(postSnap => {
            // Article does not exist
            if(!postSnap) {
                return null;
            }
            // Quick and dirty mapping to BlogPost object
            return postSnap.map(snap => new BlogPost(snap.payload.val()));
        }));
    }

    watchPost(postKey: string): Observable<BlogPost> {
        return this.db.object(MotesPaths.POST_DB_PATH(postKey)).snapshotChanges().pipe(map(postSnap => {
            // Article does not exist
            if(postKey == 'new') {
                return new BlogPost({ key: 'new', language: this.locale });
            }

            if(!postSnap.key) {
                return null;
            }
            return new BlogPost(postSnap.payload.val());
        }));
    }

    /**
     * Watch a blog post embedded by the resolver in the page
     */
    watchPostSsr(route: ActivatedRouteSnapshot, blogPostKey: string): Observable<BlogPost> {
        // Get the data from the resolver
        let pageData = route.data as { blogPost: ResolverItemStream<BlogPost> };
        // Enable pages not implementing data resolver to use the BlogPost component without SSR
        if(!pageData.blogPost) {
            console.log("Resolving blog post without SSR enabled for " + blogPostKey + ". Consider adding a resolver to app.module for better SEO.");
            return this.watchPost(blogPostKey);
        }
        // Get data embedded in page
        let firstObserver = from([pageData.blogPost.items]);
        // Get tours from data resolver and switch to live stream
        return concat(firstObserver, pageData.blogPost.stream);
    }

    savePost(post: BlogPost) {
        if(!post.key) {
            post.key = DatabaseModel.genKeyFromTitle(post.title);
            post.originKey = post.key;
            // Set or fetch post language and add it to key
            let postLang = post.language || this.locale;
            post.language = postLang;
            post.key = `${post.key}_${postLang}`;
        }
        return this.db.object(MotesPaths.POST_DB_PATH(post.key)).update(post.toJson());
    }

    deletePost(postKey: string) {
        if(!postKey) {
            return null;
        }
        return this.db.object(MotesPaths.POST_DB_PATH(postKey)).remove();
    }
}
